import React, { useEffect, useMemo, useState } from 'react';
import { Translate } from 'react-localize-redux';
import PropTypes from 'prop-types';
import { PersonAddSharp, EditOutlined } from '@mui/icons-material';
import { Button, Grid, Paper, Snackbar, Typography } from '@mui/material';
import { Alert } from '@mui/material';
import { usePrevious, useSnackBarState, useUpdateEffect } from '../../../hooks';
import PersonalDataForm from '../../../components/investigation/show/single/personal_data';
import { BoxBckgr, GridContainer } from '../../../components/general/mini_components';
import { buildDedupIndex, findDuplicatePatient, DedupResult } from '../../../utils/patientDedup';
import { resolvePersonalFieldRoles } from '../../../utils/personalFieldRoles';
import DuplicatePatientModal from './DuplicatePatientModal';
import { IField, IPatient, IPersonalData } from '../../../constants/types';

interface Props {
  investigations: any; // Replace 'any' with the appropriate type
  patients?:{loading:boolean, data:any[]}
  patient: any; // Replace 'any' with the appropriate type
  patientsInvestigation: any[]; // Replace 'any' with the appropriate type
  personalFields: any; // Replace 'any' with the appropriate type
  keyResearcherInvestigation: any; // Replace 'any' with the appropriate type
  addedPatient:boolean,
  error:number,
  callbackGoToPatient: (uuidPatient:string) => void;
  callbackGoToAppointment: (uuidPatient:string) => void;
  callbackSavePatient: (patientData:any) => void;
}

export function AddPatientComponent(props: Props) {
  const [showSnackbar, setShowSnackbar] = useSnackBarState();
  const [personalFields, setPersonalFields] = useState<IField[]>([]);
  
  const [confirmPatient, setConfirmPatient] = useState<any | null>(null);
  const [duplicateResult, setDuplicateResult] = useState<DedupResult | null>(null);
  const [lastPatient, setLastPatient] = useState<any | null>(null);


  function handleClose() {
    setShowSnackbar({ show: false });
  }

  useEffect(() => {
    const findAutomatedHealthId = props.personalFields.find((field) => field.name === 'automated_health_id');
    if(findAutomatedHealthId){
        //Hide the automated health id field from the list
        const newFields = props.personalFields.map((field) => {
            if(field.name === 'automated_health_id'){
                return {...field, type:'hidden', required:false};
            }
            return field;
        
        });
        setPersonalFields(newFields);
    }
    else{
        setPersonalFields(props.personalFields);
    }
    
  }, []);

  useEffect(() => {
        console.log("Han cambiado los pacientes");
        if(props.addedPatient){
            let messageId = '';
            if (props.patient) {
                messageId = 'hospital.patient.updated-patient';
            } else {
                messageId = 'hospital.patient.new-patient';
            }
            setShowSnackbar({ show: true, severity: 'success', message: messageId });
            setLastPatient(
                props.patientsInvestigation[props.patientsInvestigation.length - 1]
            );
        }
  }, [props.addedPatient])
  useEffect(() => {
    if(props.error !== null){
        let messageId = '';
        let severity = 'error';
        
        if (props.error === 2) {
            messageId += '-offline';
            severity = 'warning';
        } else if (props.error) {
            messageId = 'hospital.patient.error';
            severity = 'error';
        }
        setShowSnackbar({ show: true, severity: severity, message: messageId });
    }
  }, [props.error]);

    function goToPatient(){
        const uuidPatient = props.patient ? props.patient.uuid : lastPatient.uuid;
        props.callbackGoToPatient(uuidPatient)
    }

    function goToAppointment(){
        const uuidPatient = props.patient ? props.patient.uuid : lastPatient.uuid;
        props.callbackGoToAppointment(uuidPatient)
    }

// SHER-257: the phone-typed personal fields (primary `phone` + tenant-custom
// secondaries) used to index + look up by phone number. Resolved via the shared
// resolvePersonalFieldRoles so matching and the modal's display agree.
const phoneFieldNames = useMemo(
    () => resolvePersonalFieldRoles(props.personalFields).phone,
    [props.personalFields]
);
// SHER-257 (follow-up): ID-card-typed fields (e.g. a hospice's National ID card),
// resolved the same way. Empty for centres without one (CHOM stays phone-only).
const idCardFieldNames = useMemo(
    () => resolvePersonalFieldRoles(props.personalFields).idCard,
    [props.personalFields]
);

// SHER-194: build an O(1) dedup index once per patients-array reference.
// The previous implementation ran `.find()` + two console.log per patient on
// every submit, which on CHOM's ~50k-row list took 30-60s wall clock.
// The index rebuilds when Redux publishes a new patients array (incl. patient
// events synced from other users). See src/utils/patientDedup.ts.
// SHER-257: also indexes phone numbers (phoneFieldNames) and ID cards (idCardFieldNames).
const dedupIndex = useMemo(
    () => buildDedupIndex(props.patientsInvestigation as any, phoneFieldNames, idCardFieldNames),
    [props.patientsInvestigation, phoneFieldNames, idCardFieldNames]
);

async function callBackSaveUpdate(patientData:any, rawPatientData:any){
    if(!props.patient){
        const result = findDuplicatePatient(dedupIndex, rawPatientData, phoneFieldNames, idCardFieldNames);
        if(result){
            setConfirmPatient({
                rawData : rawPatientData,
                encryptedData: patientData
            });
            setDuplicateResult(result);
            return;
        }
    }
    props.callbackSavePatient(patientData)
}

function closeDuplicateModal(){
    setConfirmPatient(null);
    setDuplicateResult(null);
}

return (
    <BoxBckgr color="text.primary">
        {confirmPatient && duplicateResult &&
            <DuplicatePatientModal
                open={!!confirmPatient}
                matches={duplicateResult.matches}
                reasons={duplicateResult.reasons}
                personalFields={props.personalFields}
                closeModal={closeDuplicateModal}
                onGoBack={closeDuplicateModal}
                onReviewPatient={(uuid) => props.callbackGoToPatient(uuid)}
                onCreateAnyway={() => props.callbackSavePatient(confirmPatient.encryptedData)}
            />
        }
        <Snackbar
            anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
            }}
            open={ showSnackbar.show }
            autoHideDuration={6000}
            onClose={handleClose}>
                <Alert severity={showSnackbar.severity}>
                    <Translate id={showSnackbar.message} />
                    {
                        showSnackbar.severity === "success" &&
                        <>
                            <Button onClick={goToPatient} size="small">
                                <Translate id="pages.hospital.go-to-patient" />
                            </Button>
                            <Button onClick={goToAppointment} size="small">
                                <Translate id="pages.hospital.go-to-appointment" />
                            </Button>
                        </>
                    }
                </Alert>
            </Snackbar>
        
        <Grid container spacing={3}>
            <GridContainer item xs={12}>
                {!props.patient &&
                    <PersonAddSharp style={{fontSize:"2.5rem"}} />
                }
                {
                    props.patient &&
                    <EditOutlined style={{fontSize:"2.5rem"}} />
                }
                <Typography variant="h1" gutterBottom display="inline" style={{marginBottom:"0px"}}>
                {
                    !props.patient &&
                    <Translate id="pages.hospital.add-patient" />
                }
                {
                    props.patient &&
                    <Translate id="pages.hospital.edit-patient" />
                }
                </Typography>
            </GridContainer>
            <Grid item xs={12}>
                <Paper style={{padding:'1rem'}}>
                    <PersonalDataForm fields={ personalFields } hospital={true}
                        keyResearcherInvestigation={props.keyResearcherInvestigation}
                        submitText={props.patient ? "general.update" : null}
                        initialData={props.patient ? props.patient.personalData : {'automated_health_id': ''}} 
                        callBackForm={(personalData, rawPersonalData) => callBackSaveUpdate(personalData, rawPersonalData)}/>
                </Paper>
            </Grid>
        </Grid>
    </BoxBckgr>
    )
}

AddPatientComponent.propTypes = {
    investigations:PropTypes.object
};